import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { STORAGE_KEYS } from '@config/constants';

import type { User, UserPermissions } from '@shared/types/common.types';
import type { AuthState } from './types';

interface AuthStore extends AuthState {
  // Actions
  setUser: (user: User | null) => void;
  setPermissions: (permissions: UserPermissions | null) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  logout: () => void;
}

/**
 * Store de autenticación
 * Maneja estado de usuario, permisos y autenticación
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      set => ({
        // Estado inicial
        user: null,
        permissions: null,
        isAuthenticated: false,
        isLoading: true,

        // Setters
        setUser: user => set({ user }),
        setPermissions: permissions => set({ permissions }),
        setAuthenticated: isAuthenticated => set({ isAuthenticated }),
        setLoading: isLoading => set({ isLoading }),

        // Logout
        logout: () => {
          // Limpiar localStorage
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER);
          localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);

          // Resetear estado
          set({
            user: null,
            permissions: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },
      }),
      {
        name: 'auth-storage',
        partialize: state => ({
          user: state.user,
          permissions: state.permissions,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
